import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "@/api/axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Icons } from "@/components/icons";
import { toast } from "sonner";

const ResetPassword = () => {
	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [errors, setErrors] = useState<string[]>([]);
	const { token } = useParams();
	const navigate = useNavigate();

	const validatePassword = (password: string): string[] => {
		const validationErrors: string[] = [];

		if (password.length < 8) {
			validationErrors.push(
				"Ο κωδικός πρέπει να είναι τουλάχιστον 8 χαρακτήρες.",
			);
		}
		if (!/[A-Z]/.test(password)) {
			validationErrors.push(
				"Ο κωδικός πρέπει να περιέχει τουλάχιστον ένα κεφαλαίο γράμμα.",
			);
		}
		if (!/[a-z]/.test(password)) {
			validationErrors.push(
				"Ο κωδικός πρέπει να περιέχει τουλάχιστον ένα πεζό γράμμα.",
			);
		}
		if (!/[0-9]/.test(password)) {
			validationErrors.push(
				"Ο κωδικός πρέπει να περιέχει τουλάχιστον έναν αριθμό.",
			);
		}
		if (!/[^A-Za-z0-9]/.test(password)) {
			validationErrors.push(
				"Ο κωδικός πρέπει να περιέχει τουλάχιστον έναν ειδικό χαρακτήρα.",
			);
		}

		return validationErrors;
	};

	const handlePasswordChange = (value: string) => {
		setPassword(value);
		setErrors(validatePassword(value));
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (password !== confirmPassword) {
			toast.error("Οι κωδικοί δεν ταιριάζουν");
			return;
		}

		const validationErrors = validatePassword(password);
		if (validationErrors.length > 0) {
			setErrors(validationErrors);
			return;
		}

		setIsLoading(true);

		try {
			const response = await axios.post("/set-password", {
				token,
				password,
			});

			toast.success(
				response.data.message ||
					"Ο κωδικός ορίστηκε με επιτυχία. Μπορείτε τώρα να συνδεθείτε.",
			);

			navigate("/login");
		} catch (error: any) {
			console.error("Error setting password:", error);
			toast.error(
				error.response?.data ||
					"Σφάλμα κατά την επαναφορά του κωδικού. Το token μπορεί να έχει λήξει.",
			);
		} finally {
			setIsLoading(false);
		}
	};

	const getPasswordStrength = () => {
		if (password.length === 0) return { strength: 0, label: "" };
		if (errors.length > 3) return { strength: 1, label: "Αδύναμος" };
		if (errors.length > 1) return { strength: 2, label: "Μέτριος" };
		if (errors.length > 0) return { strength: 3, label: "Καλός" };
		return { strength: 4, label: "Ισχυρός" };
	};

	const { strength, label } = getPasswordStrength();

	return (
		<div className="container flex h-screen w-screen flex-col items-center justify-center">
			<Card className="w-[400px]">
				<CardHeader className="space-y-1">
					<CardTitle className="text-2xl text-center">
						Επαναφορά Κωδικού Πρόσβασης
					</CardTitle>
					<CardDescription className="text-center">
						Εισάγετε τον νέο σας κωδικό πρόσβασης
					</CardDescription>
				</CardHeader>
				<CardContent>
					<form onSubmit={handleSubmit} className="space-y-4">
						<div className="space-y-2">
							<Label htmlFor="password">Νέος Κωδικός Πρόσβασης</Label>
							<Input
								id="password"
								type="password"
								placeholder="••••••••"
								value={password}
								onChange={(e) => handlePasswordChange(e.target.value)}
							/>

							{password.length > 0 && (
								<div className="space-y-2">
									<div className="flex gap-1">
										{[1, 2, 3, 4].map((level) => (
											<div
												key={level}
												className={`h-2 w-full rounded ${
													level <= strength
														? level === 1
															? "bg-red-500"
															: level === 2
																? "bg-orange-500"
																: level === 3
																	? "bg-yellow-500"
																	: "bg-green-500"
														: "bg-gray-200"
												}`}
											/>
										))}
									</div>
									<p className="text-sm text-muted-foreground">
										{label && `Δύναμη Κωδικού: ${label}`}
									</p>
								</div>
							)}

							{errors.length > 0 && (
								<ul className="list-disc pl-5 text-sm text-red-500">
									{errors.map((error, index) => (
										<li key={index}>{error}</li>
									))}
								</ul>
							)}
						</div>
						<div className="space-y-2">
							<Label htmlFor="confirmPassword">Επιβεβαίωση Κωδικού</Label>
							<Input
								id="confirmPassword"
								type="password"
								placeholder="••••••••"
								value={confirmPassword}
								onChange={(e) => setConfirmPassword(e.target.value)}
							/>
							{confirmPassword && password !== confirmPassword && (
								<p className="text-sm text-red-500">
									Οι κωδικοί δεν ταιριάζουν
								</p>
							)}
						</div>
						<Button
							disabled={
								isLoading || errors.length > 0 || password !== confirmPassword
							}
							className="w-full"
							type="submit"
						>
							{isLoading ? (
								<>
									<Icons.spinner className="mr-2 h-4 w-4 animate-spin" />
									Επεξεργασία...
								</>
							) : (
								"Επαναφορά Κωδικού"
							)}
						</Button>
					</form>
				</CardContent>
			</Card>
		</div>
	);
};

export default ResetPassword;
